import React, { useState, useRef } from 'react';
import { landingAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Skeleton } from './ui/skeleton';
import { Upload, Loader2, X, Image, Link, Plus, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get full URL for display
const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

// Generic Image Upload component for landing page sections
export const LandingImageUpload = ({
  currentImageUrl,
  imageType,  // hero_banner, logo, program, feature, testimonial, location
  itemId = '',
  onUploadSuccess,
  label = 'Image',
  className = '',
  showUrlInput = true,
  aspectRatio = 'square', // square, landscape, portrait
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [urlInputValue, setUrlInputValue] = useState(currentImageUrl || '');
  const [mode, setMode] = useState('upload'); // upload or url
  const fileInputRef = useRef(null);

  const aspectClasses = {
    square: 'aspect-square',
    landscape: 'aspect-video',
    portrait: 'aspect-[3/4]',
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const response = await landingAPI.uploadImage(file, imageType, itemId);
      const imageUrl = response.data.image_url;
      toast.success('Image uploaded successfully');
      setUrlInputValue(imageUrl);
      if (onUploadSuccess) {
        onUploadSuccess(imageUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (urlInputValue && onUploadSuccess) {
      onUploadSuccess(urlInputValue);
      toast.success('Image URL updated');
    }
  };

  const clearImage = () => {
    setPreviewUrl(null);
    setUrlInputValue('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onUploadSuccess) {
      onUploadSuccess('');
    }
  };

  const displayUrl = previewUrl || getFullImageUrl(currentImageUrl);

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        {showUrlInput && (
          <div className="flex gap-1">
            <Button
              type="button"
              variant={mode === 'upload' ? 'default' : 'outline'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setMode('upload')}
            >
              <Upload className="w-3 h-3 mr-1" />
              Upload
            </Button>
            <Button
              type="button"
              variant={mode === 'url' ? 'default' : 'outline'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setMode('url')}
            >
              <Link className="w-3 h-3 mr-1" />
              URL
            </Button>
          </div>
        )}
      </div>

      {mode === 'upload' ? (
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileSelect}
            className="hidden"
            data-testid={`image-upload-${imageType}-${itemId}`}
          />

          <div
            className={`relative ${aspectClasses[aspectRatio]} max-h-48 rounded-lg overflow-hidden bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-primary hover:bg-slate-50 transition-all group`}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="text-sm text-slate-500">Uploading...</span>
              </div>
            ) : displayUrl ? (
              <>
                <img
                  src={displayUrl}
                  alt={label}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Upload className="w-6 h-6 text-white" />
                  <span className="text-white text-sm font-medium">Change Image</span>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-2 p-4">
                <Image className="w-8 h-8 text-slate-400" />
                <span className="text-sm text-slate-500 text-center">Click to upload image</span>
                <span className="text-xs text-slate-400">JPEG, PNG, WebP, GIF (max 5MB)</span>
              </div>
            )}
          </div>

          {displayUrl && !uploading && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                clearImage();
              }}
            >
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={urlInputValue}
            onChange={(e) => setUrlInputValue(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="flex-1"
          />
          <Button
            type="button"
            size="sm"
            onClick={handleUrlSubmit}
            disabled={!urlInputValue}
          >
            Apply
          </Button>
        </div>
      )}

      {urlInputValue && mode === 'url' && (
        <div className={`${aspectClasses[aspectRatio]} max-h-32 rounded-lg overflow-hidden bg-slate-100`}>
          <img
            src={getFullImageUrl(urlInputValue)}
            alt={label}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
};

// Logo upload component with circular preview
export const LogoUpload = ({
  currentLogoUrl,
  onUploadSuccess,
  className = '',
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file size must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const response = await landingAPI.uploadImage(file, 'logo', '');
      const imageUrl = response.data.image_url;
      toast.success('Logo uploaded successfully');
      if (onUploadSuccess) {
        onUploadSuccess(imageUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const displayUrl = previewUrl || getFullImageUrl(currentLogoUrl);

  return (
    <div className={`space-y-2 ${className}`}>
      <Label className="text-sm font-medium">Academy Logo</Label>
      <div className="flex items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="logo-upload"
        />

        <div
          className="relative w-20 h-20 rounded-xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-primary transition-colors group"
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          ) : displayUrl ? (
            <>
              <img src={displayUrl} alt="Logo" className="w-full h-full object-contain p-1" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Upload className="w-5 h-5 text-white" />
              </div>
            </>
          ) : (
            <Image className="w-6 h-6 text-slate-400" />
          )}
        </div>

        <div className="flex-1">
          <p className="text-sm text-slate-600">Click to upload your academy logo</p>
          <p className="text-xs text-slate-400">PNG or WebP recommended (max 2MB)</p>
          {displayUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-1 h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 p-0"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewUrl(null);
                if (onUploadSuccess) onUploadSuccess('');
              }}
            >
              Remove logo
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Carousel/Multi-Image Upload component with thumbnails, remove, and lazy loading
export const CarouselImageUpload = ({
  currentImages = [], // Array of image URLs
  onImagesChange,     // Callback with updated array of URLs
  imageType = 'carousel',
  itemId = '',
  label = 'Carousel Images',
  className = '',
  maxImages = 10,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [urlInputValue, setUrlInputValue] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [errorImages, setErrorImages] = useState({});
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Check max images
    const availableSlots = maxImages - currentImages.length;
    if (availableSlots <= 0) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    const filesToUpload = files.slice(0, availableSlots);
    if (files.length > availableSlots) {
      toast.warning(`Only uploading ${availableSlots} of ${files.length} images (max ${maxImages})`);
    }

    // Validate all files
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    for (const file of filesToUpload) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}. Use JPEG, PNG, WebP, or GIF.`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File too large: ${file.name}. Max 5MB per image.`);
        return;
      }
    }

    setUploading(true);
    const newUrls = [...currentImages];

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setUploadingIndex(newUrls.length);
      
      try {
        const response = await landingAPI.uploadImage(file, imageType, itemId);
        const imageUrl = response.data.image_url;
        newUrls.push(imageUrl);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    setUploadingIndex(null);
    
    if (newUrls.length > currentImages.length) {
      toast.success(`${newUrls.length - currentImages.length} image(s) uploaded`);
      onImagesChange(newUrls);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlAdd = () => {
    if (!urlInputValue.trim()) return;
    
    const url = urlInputValue.trim();
    if (currentImages.includes(url)) {
      toast.error('This URL is already added');
      return;
    }

    if (currentImages.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    onImagesChange([...currentImages, url]);
    setUrlInputValue('');
    setShowUrlInput(false);
    toast.success('Image URL added');
  };

  const handleRemoveImage = (indexToRemove) => {
    const newImages = currentImages.filter((_, idx) => idx !== indexToRemove);
    // Clear loaded/error state for removed image
    const url = currentImages[indexToRemove];
    setLoadedImages(prev => {
      const updated = { ...prev };
      delete updated[url];
      return updated;
    });
    setErrorImages(prev => {
      const updated = { ...prev };
      delete updated[url];
      return updated;
    });
    onImagesChange(newImages);
    toast.success('Image removed');
  };

  const handleImageLoad = (url) => {
    setLoadedImages(prev => ({ ...prev, [url]: true }));
  };

  const handleImageError = (url) => {
    setErrorImages(prev => ({ ...prev, [url]: true }));
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= currentImages.length) return;
    const newImages = [...currentImages];
    const [removed] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, removed);
    onImagesChange(newImages);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <span className="text-xs text-slate-500">{currentImages.length}/{maxImages} images</span>
      </div>

      {/* Image Thumbnails Grid */}
      {currentImages.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {currentImages.map((url, idx) => {
            const isLoaded = loadedImages[url];
            const hasError = errorImages[url];
            const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
            
            return (
              <div
                key={`${url}-${idx}`}
                className="relative aspect-video rounded-lg overflow-hidden bg-slate-100 border border-slate-200 group"
              >
                {/* Loading skeleton */}
                {!isLoaded && !hasError && (
                  <div className="absolute inset-0 z-10">
                    <Skeleton className="w-full h-full rounded-none" />
                  </div>
                )}
                
                {/* Error fallback */}
                {hasError ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 p-2">
                    <Image className="w-6 h-6 mb-1 opacity-50" />
                    <span className="text-xs text-center">Failed to load</span>
                  </div>
                ) : (
                  <img
                    src={fullUrl}
                    alt={`Carousel ${idx + 1}`}
                    loading="lazy"
                    onLoad={() => handleImageLoad(url)}
                    onError={() => handleImageError(url)}
                    className={`w-full h-full object-cover transition-opacity ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                  />
                )}

                {/* Hover overlay with actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  {/* Move buttons */}
                  {idx > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-white hover:bg-white/20"
                      onClick={() => moveImage(idx, idx - 1)}
                      title="Move left"
                    >
                      ←
                    </Button>
                  )}
                  <span className="text-white text-xs font-medium px-1">{idx + 1}</span>
                  {idx < currentImages.length - 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-white hover:bg-white/20"
                      onClick={() => moveImage(idx, idx + 1)}
                      title="Move right"
                    >
                      →
                    </Button>
                  )}
                </div>

                {/* Remove button */}
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20"
                  onClick={() => handleRemoveImage(idx)}
                  data-testid={`carousel-remove-${idx}`}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            );
          })}

          {/* Uploading placeholder */}
          {uploading && uploadingIndex !== null && (
            <div className="aspect-video rounded-lg bg-slate-100 border-2 border-dashed border-primary flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}
        </div>
      )}

      {/* Upload Actions */}
      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          data-testid="carousel-image-upload"
        />

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || currentImages.length >= maxImages}
          className="flex-1 sm:flex-none"
          data-testid="carousel-add-image-btn"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          {uploading ? 'Uploading...' : 'Add Images'}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowUrlInput(!showUrlInput)}
          disabled={currentImages.length >= maxImages}
          className="text-slate-500"
        >
          <Link className="w-4 h-4 mr-1" />
          Add via URL
        </Button>
      </div>

      {/* URL Input */}
      {showUrlInput && (
        <div className="flex gap-2">
          <Input
            value={urlInputValue}
            onChange={(e) => setUrlInputValue(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="flex-1 text-sm"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleUrlAdd())}
          />
          <Button
            type="button"
            size="sm"
            onClick={handleUrlAdd}
            disabled={!urlInputValue.trim()}
          >
            Add
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { setShowUrlInput(false); setUrlInputValue(''); }}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Helper text */}
      {currentImages.length === 0 && !uploading && (
        <p className="text-xs text-slate-400 text-center py-4 border-2 border-dashed rounded-lg">
          No images added. Click "Add Images" to upload carousel images.
        </p>
      )}
    </div>
  );
};

export default LandingImageUpload;
